/**
 * A product an organisation can be licensed for.
 *
 * Mirrors the engine's `product_key` Postgres enum — these are its wire values,
 * so a key absent here is one the engine would reject. Adding one is a
 * migration on that side before it is a change on this one.
 *
 * A licence means the customer's results for the product have been released to
 * them. Business Central remains the system of record for the commercial
 * agreement behind it.
 */
export enum ProductKey {
    GLOBAL_NATURE_RISK = 'global_nature_risk',
    HABITAT_INSIGHTS = 'nature_performance_habitat_insights',
    SPECIES_INSIGHTS = 'nature_performance_species_insights',
    EDNA = 'nature_performance_edna',
    /** The portfolio pages. An organisation has results when one of its projects is in a portfolio. */
    GLOBAL_NATURE_PERFORMANCE = 'global_nature_performance',
}

/** The name users see for each product. A `Record`, so a new key needs a name. */
export const PRODUCT_NAMES: Record<ProductKey, string> = {
    [ProductKey.GLOBAL_NATURE_RISK]: 'Global Nature Risk',
    [ProductKey.HABITAT_INSIGHTS]: 'Habitat Insights',
    [ProductKey.SPECIES_INSIGHTS]: 'Species Insights',
    [ProductKey.EDNA]: 'eDNA results',
    [ProductKey.GLOBAL_NATURE_PERFORMANCE]: 'Global Nature Performance',
};

/**
 * Who releases a product's results to the customer. Mirrors the engine's
 * `ReleasePolicy`, which refuses Visible for an automatic product. Nothing
 * checks that the two lists agree.
 */
export enum ReleasePolicy {
    /** The results show only when the product is set to Visible. */
    MANUAL = 'manual',
    /** The results appear when the site has them. Only the Hidden setting can withhold them. */
    AUTOMATIC = 'automatic',
}

/** A `Record`, so a new key does not compile until someone says how it is released. */
export const PRODUCT_RELEASE: Record<ProductKey, ReleasePolicy> = {
    [ProductKey.GLOBAL_NATURE_RISK]: ReleasePolicy.MANUAL,
    [ProductKey.HABITAT_INSIGHTS]: ReleasePolicy.AUTOMATIC,
    [ProductKey.SPECIES_INSIGHTS]: ReleasePolicy.AUTOMATIC,
    [ProductKey.EDNA]: ReleasePolicy.AUTOMATIC,
    [ProductKey.GLOBAL_NATURE_PERFORMANCE]: ReleasePolicy.AUTOMATIC,
};

/**
 * Whether a product has an upsell page that a customer sees where there are no
 * results. A `Record`, so a new key does not compile until someone says.
 */
export const PRODUCT_HAS_UPSELL_PAGE: Record<ProductKey, boolean> = {
    [ProductKey.GLOBAL_NATURE_RISK]: true,
    [ProductKey.HABITAT_INSIGHTS]: true,
    [ProductKey.SPECIES_INSIGHTS]: true,
    [ProductKey.EDNA]: false,
    [ProductKey.GLOBAL_NATURE_PERFORMANCE]: false,
};

/**
 * Whether each project also turns the product on, in its Project Definition.
 * The setting is for the whole organisation, so such a product shows only on
 * the projects that have it turned on. A `Record`, so a new key does not
 * compile until someone says.
 */
export const PRODUCT_TURNED_ON_PER_PROJECT: Record<ProductKey, boolean> = {
    [ProductKey.GLOBAL_NATURE_RISK]: false,
    [ProductKey.HABITAT_INSIGHTS]: true,
    [ProductKey.SPECIES_INSIGHTS]: true,
    [ProductKey.EDNA]: false,
    [ProductKey.GLOBAL_NATURE_PERFORMANCE]: false,
};

/**
 * The setting of a product in product licensing management: how much of the
 * product an organisation's own users see. Mirrors the engine's
 * `product_visibility` Postgres enum.
 *
 * There is no value for the default. The engine stores the default as the
 * absence of a setting, so the default has one representation and this app is
 * where it is named.
 */
export enum ProductVisibility {
    HIDDEN = 'hidden',
    VISIBLE = 'visible',
}

/**
 * The setting of a product for one organisation, as product licensing
 * management shows it. `DEFAULT` means that the product has no setting: a
 * manual product shows its upsell page, and an automatic product releases its
 * results when the site has them.
 */
export enum ProductVisibilityOutcome {
    HIDDEN = ProductVisibility.HIDDEN,
    VISIBLE = ProductVisibility.VISIBLE,
    DEFAULT = 'default',
}

export function asProductVisibilityOutcome(setting: ProductVisibility): ProductVisibilityOutcome {
    if (setting === ProductVisibility.HIDDEN) {
        return ProductVisibilityOutcome.HIDDEN;
    } else {
        return ProductVisibilityOutcome.VISIBLE;
    }
}

/** The product settings of one organisation. A product with no entry has the default. */
export type ProductVisibilityMatrix = Partial<Record<ProductKey, ProductVisibility>>;
